import { useState } from 'react';
import { AssistantActionKind, type AssistantActionProposal } from '@ddp/shared';
import Button from '@carbon/react/es/components/Button';
import CheckmarkFilled from '@carbon/icons-react/es/CheckmarkFilled';

import { apiFetch } from '../../api/client.js';
import { useT } from '../../i18n/index.js';

/**
 * Carte d'**action proposée** par l'assistant (ADR-0027, incrément 2). L'assistant n'exécute rien :
 * c'est le clic de l'utilisateur, ici, qui déclenche l'appel à l'**endpoint normal** — gardé par son
 * rôle et journalisé côté API. La confirmation est la frontière de sécurité, pas une politesse d'UX.
 */
export function ProposalCard({
  proposal,
  canAct,
}: {
  proposal: AssistantActionProposal;
  canAct: boolean;
}) {
  const t = useT();
  const [status, setStatus] = useState<'idle' | 'pending' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setStatus('pending');
    setError(null);
    try {
      // Une seule action pour l'instant (adoption de modèle) ; le `kind` route vers l'endpoint réel.
      if (proposal.kind === AssistantActionKind.ADOPT_LIBRARY_MODEL) {
        await apiFetch('/models/adopt', {
          method: 'POST',
          body: { libraryItemId: proposal.libraryItemId },
        });
      }
      setStatus('done');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : t('assistant.adoptFailed'));
    }
  };

  return (
    <div className="ddp-assistant__proposal">
      <span className="ddp-assistant__proposal-label">{proposal.label}</span>

      {status === 'done' ? (
        <span className="ddp-assistant__proposal-done">
          <CheckmarkFilled size={16} /> {t('assistant.adopted')}
        </span>
      ) : canAct ? (
        <Button
          kind="tertiary"
          size="sm"
          disabled={status === 'pending'}
          onClick={() => void confirm()}
        >
          {status === 'pending' ? t('assistant.adopting') : t('assistant.adopt')}
        </Button>
      ) : (
        <span className="ddp-assistant__proposal-hint">{t('assistant.adoptNeedsEditor')}</span>
      )}

      {status === 'error' && error && (
        <span className="ddp-assistant__proposal-error">{error}</span>
      )}
    </div>
  );
}
